import { ListIllustration } from "vega-illustrations";

import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { useGetInsights } from "@vega-api/generated/insights/insights";
import { type BarChartMetric } from "@vega-api/generated/types/barChartMetric";
import { type PieChartMetric } from "@vega-api/generated/types/pieChartMetric";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import { BarChartMetrics } from "../components/BarChartMetrics/BarChartMetrics";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { OrganisationsTable } from "../components/OrganisationsTable/OrganisationsTable";
import { PieChartMetrics } from "../components/PieChartMetrics/PieChartMetrics";
import { useInsightsLiveRefresh } from "../hooks/useInsightsLiveRefresh";
import { isBarChartMetric, isPieChartMetric } from "../utils/metricGuards";

import { ReportSectionMessage } from "./components/ReportSectionMessage";

type InsightsSelection = {
  barChartMetrics: BarChartMetric[];
  pieChartMetrics: PieChartMetric[];
};

const EMPTY_INSIGHTS: InsightsSelection = {
  barChartMetrics: [],
  pieChartMetrics: [],
};

export const InsightsPage = () => {
  useFeatureFlagGuard(
    FeatureFlag.COINVEST_INSIGHTS_VD_26155,
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  useInsightsLiveRefresh();

  const {
    data: insights,
    isPending,
    isError,
    refetch,
  } = useGetInsights<InsightsSelection | undefined>({
    query: {
      select: (response): InsightsSelection => {
        const barChartMetrics: BarChartMetric[] = [];
        const pieChartMetrics: PieChartMetric[] = [];

        for (const metric of response.data.overview) {
          if (isBarChartMetric(metric)) {
            barChartMetrics.push(metric);
          } else if (isPieChartMetric(metric)) {
            pieChartMetrics.push(metric);
          }
        }

        return { barChartMetrics, pieChartMetrics };
      },
    },
  });

  const { barChartMetrics, pieChartMetrics } = insights ?? EMPTY_INSIGHTS;

  // Charts and the organisations table are independent data sources, so each
  // owns its own loading/empty/error; the table always renders below the charts.
  const renderCharts = () => {
    if (isError) {
      return (
        <ReportSectionMessage
          dataTestId="insights-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={refetch}
        />
      );
    }

    if (
      !isPending &&
      barChartMetrics.length === 0 &&
      pieChartMetrics.length === 0
    ) {
      return (
        <EmptyCard
          description={m.coInvestments_insights_empty_subtitle()}
          illustration={<ListIllustration />}
          title={m.coInvestments_insights_empty_title()}
        />
      );
    }

    return (
      <>
        <BarChartMetrics
          barChartMetrics={barChartMetrics}
          isLoading={isPending}
        />
        <PieChartMetrics
          centerLabel={m.coInvestments_insights_organisationsTotal()}
          className="xl:grid-cols-2"
          dataTestId="insights-pie-charts"
          isLoading={isPending}
          legendVariant="double"
          loadingCount={2}
          pieChartMetrics={pieChartMetrics}
        />
      </>
    );
  };

  return (
    <div className="mt-1 pb-52">
      <CoInvestmentPageHeader
        showDrawer={false}
        title={m.coInvestments_insights_title()}
      />
      <div className="mt-6 space-y-6">
        {renderCharts()}
        <OrganisationsTable />
      </div>
    </div>
  );
};
